import { Bluetooth } from 'lucide-react'
import { BRAND, SCAN_HERO_BG, SCAN_HERO_SOFT } from '@/constants/brand'
import { AppButton } from './AppButton'
import { cn } from '@/utils/cn'

export type ScanSessionHeroProps = {
  totalTags: number
  overallProgress: number
  linesComplete?: number
  linesTotal?: number
  scannerConnected: boolean
  scannerType?: string | null
  onConnectScanner?: () => void
  className?: string
}

export function ScanSessionHero({
  totalTags,
  overallProgress,
  linesComplete = 0,
  linesTotal = 0,
  scannerConnected,
  scannerType,
  onConnectScanner,
  className,
}: ScanSessionHeroProps) {
  const label = scannerType ?? 'Scanner'
  const statusText = scannerConnected
    ? `${label} active`
    : `${label} not connected`
  const pct = Math.round(Math.min(1, Math.max(0, overallProgress)) * 100)
  const linesCaption =
    linesTotal > 0 ? `${linesComplete} of ${linesTotal} lines complete` : undefined

  return (
    <div
      className={cn('rfid-scan-hero', className)}
      style={{ background: `linear-gradient(160deg, ${SCAN_HERO_BG} 0%, ${SCAN_HERO_SOFT} 100%)` }}
    >
      <div className="mb-4 flex items-center gap-2.5">
        <span className="relative inline-flex h-3 w-3 items-center justify-center">
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              scannerConnected ? 'bg-emerald-500' : 'bg-white/30'
            )}
          />
          {scannerConnected ? (
            <span className="absolute inset-0 rounded-full border-[1.5px] border-emerald-500 opacity-55" />
          ) : null}
        </span>
        <span className="text-[10.5px] font-bold leading-[14px] text-white/70">{statusText}</span>
      </div>

      <div className="mb-1 text-center">
        <div className="rfid-scan-hero__count">{totalTags}</div>
        <div className="mt-0.5 text-[11.5px] font-bold uppercase tracking-wide text-white/50">
          Total tags detected
        </div>
      </div>

      <div className="mt-3.5 mb-3.5">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <span className="text-[10px] font-bold text-white/60">Scan progress</span>
          <span className="text-[11px] font-extrabold text-white">{pct}%</span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full rounded-full bg-emerald-500 transition-all duration-300"
            style={{ width: `${pct}%` }}
          />
        </div>
        {linesCaption ? (
          <div className="mt-1.5 text-[10px] font-semibold text-white/50">{linesCaption}</div>
        ) : null}
      </div>

      {scannerConnected ? (
        <div
          className="mb-1 rounded-xl border border-white/[0.06] p-3 text-[11px] font-semibold leading-4 text-white/85"
          style={{ backgroundColor: SCAN_HERO_SOFT }}
        >
          Keep scanning materials. Tags will allocate to matching lines automatically.
        </div>
      ) : (
        <p className="mb-1 text-center text-[11px] font-semibold leading-4 text-white/70">
          Connect a scanner to begin capturing RFID tags.
        </p>
      )}

      {onConnectScanner ? (
        <AppButton
          onClick={onConnectScanner}
          className="mt-3 w-full !rounded-[10px] !shadow-none"
          style={{ backgroundColor: BRAND }}
        >
          <Bluetooth size={16} strokeWidth={2.2} />
          Go to scanner connect
        </AppButton>
      ) : null}
    </div>
  )
}
